import { useState, type FormEvent } from 'react';

import { Button, Input, StatusPill } from '@/design-system';
import {
    publicReportProblemOf,
    type PublicLanguage,
    type PublicSharing,
} from '@/entities/publicReport';

import type { PublicCopy } from '../copy';
import { formatDate, looksLikeEmail } from '../format';
import { PublicBlock } from './PublicBlock';
import { SectionTitle } from './SectionTitle';

export interface ShareBlockProps {
    sharing: PublicSharing;
    language: PublicLanguage;
    copy: PublicCopy;
    headingLevel: 2 | 3;
    onShare: (email: string) => Promise<void>;
    /**
     * Alleen in de admin-preview: het blok is te zien zoals de koper het ziet,
     * maar doorsturen kan niet - deze regel zegt waarom.
     */
    previewNote?: string;
}

/**
 * Doorsturen is het distributiekanaal: elke gedeelde link brengt een makelaar
 * of adviseur binnen. Staat doorsturen uit bij het bedrijf, dan is het blok weg.
 */
export function ShareBlock({
    sharing,
    language,
    copy,
    headingLevel,
    onShare,
    previewNote,
}: ShareBlockProps) {
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmation, setConfirmation] = useState<string | null>(null);

    if (!sharing.enabled) {
        return null;
    }

    async function submit(event: FormEvent) {
        event.preventDefault();

        if (previewNote) {
            return;
        }

        const value = email.trim();

        if (!looksLikeEmail(value)) {
            setError(copy.shareInvalid);
            setConfirmation(null);

            return;
        }

        setBusy(true);
        setError(null);

        try {
            await onShare(value);
            setConfirmation(copy.shareSent(value));
            setEmail('');
        } catch (failure) {
            setError(shareErrorMessage(failure, copy));
        } finally {
            setBusy(false);
        }
    }

    return (
        <PublicBlock>
            <SectionTitle level={headingLevel} description={copy.shareSubtitle}>
                {copy.shareTitle}
            </SectionTitle>

            <form onSubmit={submit} className="flex flex-wrap gap-2" noValidate>
                <Input
                    type="email"
                    size="lg"
                    inputMode="email"
                    autoComplete="email"
                    aria-label={copy.shareEmailLabel}
                    aria-invalid={error ? true : undefined}
                    value={email}
                    placeholder={copy.sharePlaceholder}
                    className="flex-[1_1_220px]"
                    onChange={(event) => setEmail(event.target.value)}
                />
                <Button
                    type="submit"
                    variant="accentSoft"
                    size="lg"
                    disabled={busy || previewNote !== undefined}
                >
                    {copy.shareSubmit}
                </Button>
            </form>

            {previewNote ? (
                <p className="mt-2 mb-0 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {previewNote}
                </p>
            ) : null}

            {error ? (
                <p
                    role="alert"
                    className="mt-2 mb-0 text-[12px] leading-[1.4] text-[var(--danger)]"
                >
                    {error}
                </p>
            ) : null}

            {confirmation ? (
                <p
                    role="status"
                    className="mt-3 mb-0 text-[13px] leading-[1.5] text-[var(--accent-dark)]"
                >
                    {confirmation}
                </p>
            ) : null}

            {sharing.recipients.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                    {sharing.recipients.map((recipient) => {
                        const moment = formatDate(recipient.sharedAt, language);

                        return (
                            <StatusPill
                                key={recipient.name + (recipient.sharedAt ?? '')}
                                tone="accent"
                                size="lg"
                            >
                                {moment
                                    ? `${copy.sharedWith(recipient.name)} · ${moment}`
                                    : copy.sharedWith(recipient.name)}
                            </StatusPill>
                        );
                    })}
                </div>
            ) : null}
        </PublicBlock>
    );
}

/** Waarom doorsturen niet lukte, in de woorden van de koper. */
function shareErrorMessage(failure: unknown, copy: PublicCopy): string {
    switch (publicReportProblemOf(failure).kind) {
        case 'forwardingDisabled':
            return copy.shareDisabled;
        case 'tooManyAttempts':
            return copy.shareTooMany;
        case 'invalidInput':
            return copy.shareInvalid;
        case 'offline':
            return copy.problemBodies.offline;
        default:
            return copy.actionFailed;
    }
}
